import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useDebounce } from '@/shared/hooks';
import { getLanguageDisplayName } from '@/shared/lib';
import { DropdownSelect, Input, OptionType } from '@/shared/ui';

import { listLanguagesWithSelected } from '@/entities/transcriptor';

import { Filters } from '@/features/filters';

import {
  COMMUNITY_CATALOG_PARAM,
  i18n,
  LANGUAGE_FILTER_ALL,
  SEARCH_DEBOUNCE_MS,
  SOURCE_FILTER_ALL,
  SOURCE_FILTER_VALUES,
  SourceFilterValue,
} from '../../config';
import { useCommunityCatalogParams } from '../../lib';

import styles from './CommunityCatalogFilters.module.scss';

type CommunityCatalogFiltersProps = {
  availableLanguages: string[];
  language: string;
  className?: string;
};

export const CommunityCatalogFilters: FC<CommunityCatalogFiltersProps> = (
  props,
) => {
  const { availableLanguages, language, className } = props;

  const { formatMessage, locale } = useIntl();
  const { params, setQuery, setLanguage } = useCommunityCatalogParams();
  const { query } = params;

  const [localQuery, setLocalQuery] = useState(query);
  const debouncedQuery = useDebounce(localQuery, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedQuery !== query) {
      setQuery(debouncedQuery);
    }
  }, [debouncedQuery, query, setQuery]);

  useEffect(() => {
    setLocalQuery(query);
  }, [query]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalQuery(event.target.value);
  };

  const handleLanguageChange = (newValue: string | string[]) => {
    if (typeof newValue === 'string') {
      setLanguage(newValue);
    }
  };

  const sourceFilterItems = SOURCE_FILTER_VALUES.map((value) => ({
    value,
    label: formatMessage(i18n.sourceFilterMessages[value]),
  }));

  const languageOptions: OptionType[] = [
    {
      value: LANGUAGE_FILTER_ALL,
      label: formatMessage({
        id: 'global.all',
        defaultMessage: 'All',
      }),
    },
    ...listLanguagesWithSelected(availableLanguages, language).map((code) => ({
      value: code,
      label: getLanguageDisplayName(code, locale),
    })),
  ];

  return (
    <div
      className={cn(styles.container, className)}
      data-section="community-catalog-filters"
    >
      <Filters<SourceFilterValue>
        className={styles.sourceFilters}
        filterName={COMMUNITY_CATALOG_PARAM.source}
        defaultActiveKey={SOURCE_FILTER_ALL}
        items={sourceFilterItems}
      />

      <Input
        name="communityCatalogSearch"
        type="search"
        inputMode="search"
        autoComplete="off"
        value={localQuery}
        onChange={handleSearchChange}
        onResetField={() => setLocalQuery('')}
        placeholder={formatMessage({
          id: 'communityCatalog.searchPlaceholder',
          defaultMessage: 'Search by name or description',
        })}
        icon="search"
        className={styles.search}
        inputWrapperClassName={styles.searchWrapper}
        inputClassName={styles.searchInput}
      />

      <DropdownSelect
        className={styles.languageFilter}
        label={formatMessage({
          id: 'communityCatalog.languageLabel',
          defaultMessage: 'Language',
        })}
        options={languageOptions}
        value={language}
        onChange={handleLanguageChange}
      />
    </div>
  );
};
